<template>
  <v-app id="app" light>
    <confirm-dialog ref="confirm" />
    <template v-if="showApplication">
      <template v-if="loadingOverlay">
        <v-fade-transition>
          <div class="loading-overlay white">
            <v-row align="center" class="fill-height" justify="center">
              <v-col class="text-center" cols="4">
                <hb-loading-indicator :align-middle="true" />
              </v-col>
            </v-row>
          </div>
        </v-fade-transition>
      </template>

      <v-snackbar v-model="snackbar" :color="notification.color" :timeout="notification.timeOut" multi-line top>
        {{ notification.text }}
        <template v-slot:action>
          <template v-if="notification.showButton">
            <v-btn
              outlined
              small
              text
              @click="
                () => {
                  notification.callback();
                  snackbar = false;
                }
              "
            >
              {{ notification.buttonText }}
            </v-btn>
          </template>
          <template v-else>
            <v-btn icon @click="snackbar = false">
              <v-icon>close</v-icon>
            </v-btn>
          </template>
        </template>
      </v-snackbar>

      <v-navigation-drawer
        v-if="shouldMenuBeAvailable"
        v-model="menuOpenState"
        :bottom="$vuetify.breakpoint.smAndDown"
        :mini-variant="miniMenu && $vuetify.breakpoint.lgAndUp"
        app
        color="white"
        fixed
        light
        width="300"
      >
        <v-list class="pa-0 white" light>
          <v-list-item>
            <v-list-item-avatar
              v-if="miniMenu && $vuetify.breakpoint.lgAndUp"
              color="transparent"
              style="margin-left: -8px; margin-top: 10px; margin-bottom: 10px"
              tile
              @click.stop="toggleMiniMenu"
            >
              <img src="/img/EKD-logo-white.png" style="height: 35px; width: auto; margin: auto" />
            </v-list-item-avatar>
            <v-list-item-content v-else class="pt-3 pb-1">
              <v-list-item-title class="subheading font-weight-bold px-1" @click.stop="toggleMiniMenu">
                <img :alt="$t('title')" src="/img/EKD-logo-white.png" style="height: auto; width: 160px; margin: auto; margin-left: 46px" />
              </v-list-item-title>
              <!--<v-list-item-subtitle class="caption" v-if="$isLoggedIn">{{ $userInfo.email }}</v-list-item-subtitle>-->
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-list class="pt-0 main-menu">
          <v-expand-transition mode="in-out">
            <v-list-item
              v-if="onboardingStep > -1 && onboardingStep < 7 && !miniMenu"
              :to="{ name: 'tenant_onboarding' }"
              class="caption white"
              dark
              style="min-height: auto"
            >
              <v-list-item-content>
                <div>
                  <div class="ml-1 mb-1 secondary--text">
                    {{ $t('onboarding.onboarding_complete', { 0: onboardingProgress }) }}
                  </div>
                  <v-progress-linear :value="onboardingProgress" class="caption" color="accent3" height="4" rounded></v-progress-linear>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-expand-transition>

          <template v-if="$userInfo && $userInfo.tenants.length > 1">
            <TenantSelect :miniMenu="miniMenu" />
          </template>
          <template>
            <v-list-item>
              <v-list-item-action v-if="miniMenu && $vuetify.breakpoint.lgAndUp">
                <v-menu bottom right>
                  <template v-slot:activator="{ on }">
                    <v-avatar size="34" style="margin-left: -4px" v-on="on">
                      <span
                        :class="'fi-' + languages.find(l => l.value === userLanguage).icon"
                        class="fi fis"
                        style="height: 34px; width: 34px"
                      ></span>
                    </v-avatar>
                  </template>
                  <v-list class="pt-0 pb-0" dense min-width="140">
                    <v-subheader>Choose language:</v-subheader>
                    <v-list-item v-for="l in languages" :key="l.value" @click="changeUserLanguage(l.value)">
                      <v-list-item-icon class="mr-4">
                        <span :class="'fi-' + l.icon" class="fi"></span>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">{{ l.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
              <v-select
                v-else
                :items="languages"
                :loading="loadingUserLanguage"
                :value="userLanguage"
                class="my-3 language-select"
                dense
                hide-details
                label="Language"
                outlined
                @change="changeUserLanguage"
              >
                <template v-slot:item="{ item }">
                  <!--<v-list-item-icon class="mr-4">
                    <span :class="'fi-' + item.icon" class="fi"></span>
                  </v-list-item-icon>-->
                  <v-list-item-content>
                    <v-list-item-title class="secondary--text">{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  <!--<span :class="'fi-' + item.icon" class="fi mr-4"></span>-->
                  <span>{{ item.text }}</span>
                </template>
              </v-select>
            </v-list-item>
          </template>

          <template v-if="filteredPrimaryPages.length > 0">
            <PageLink
              v-for="(page, index) in filteredPrimaryPages"
              :key="index + 'primary'"
              :mini-menu="miniMenu"
              :page="page"
              @click="closeMenuIfOnSamePage(page.link)"
            ></PageLink>
            <v-divider></v-divider>
          </template>

          <template v-if="filteredSecondaryPages.length > 0">
            <PageLink
              v-for="(page, index) in filteredSecondaryPages"
              :key="index + 'secondary'"
              :mini-menu="miniMenu"
              :page="page"
              @click="closeMenuIfOnSamePage(page.link)"
            ></PageLink>
            <v-divider></v-divider>
          </template>

          <template v-if="filteredTenantAdminPages.length > 0">
            <v-subheader v-if="!miniMenu" class="primary--text">{{ $t('menu_subheaders.organization_admin') }} </v-subheader>
            <PageLink
              v-for="(page, index) in filteredTenantAdminPages"
              :key="index + 'tadmin'"
              :mini-menu="miniMenu"
              :page="page"
              @click="closeMenuIfOnSamePage(page.link)"
            ></PageLink>
            <v-divider></v-divider>
          </template>

          <template v-if="filteredAdminPages.length > 0">
            <v-subheader v-if="!miniMenu" class="primary--text">{{ $t('menu_subheaders.application_admin') }} </v-subheader>
            <PageLink
              v-for="(page, index) in filteredAdminPages"
              :key="index + 'admin'"
              :mini-menu="miniMenu"
              :page="page"
              @click="closeMenuIfOnSamePage(page.link)"
            ></PageLink>
            <v-divider></v-divider>
          </template>

          <v-subheader v-if="!miniMenu" class="primary--text">{{ $t('user_profile.user_settings') }} </v-subheader>

          <PageLink
            :mini-menu="miniMenu"
            :page="{ title: $t('user_profile.title'), icon: 'person', link: 'userprofile' }"
            @click="closeMenuIfOnSamePage('userprofile')"
          ></PageLink>
          <PageLink :mini-menu="miniMenu" :page="{ title: $t('login.logout'), icon: 'logout', link: 'logout' }" @click="logout"></PageLink>
        </v-list>
      </v-navigation-drawer>
      <v-main app>
        <router-view />
      </v-main>
    </template>
    <template v-else>
      <new-account-view />
    </template>
  </v-app>
</template>

<script>
import TenantSelect from './components/TenantSelect.vue';
import TenantService from './service/tenant.service';
import UiEventBus from '@/UiEventBus';
import PageLink from '@/components/App/PageLink';
import dayjs from 'dayjs';
import 'dayjs/locale/fi';
import 'dayjs/locale/en';

import * as Sentry from '@sentry/vue';
import ConfirmDialog from '@/components/ConfirmDialog';

import UserService from '@/service/user.service';
import TranslationService from '@/service/translation.service';
import NewAccountView from '@/views/NewAccountView';
import '/node_modules/flag-icons/css/flag-icons.min.css';

const advancedFormat = require('dayjs/plugin/advancedFormat');
const isoWeek = require('dayjs/plugin/isoWeek');
const customParseFormat = require('dayjs/plugin/customParseFormat');
const calendar = require('dayjs/plugin/calendar');
const duration = require('dayjs/plugin/duration');
const isBetween = require('dayjs/plugin/isBetween');
const objectSupport = require('dayjs/plugin/objectSupport');
const isSameOrBefore = require('dayjs/plugin/isSameOrBefore');

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(calendar);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(objectSupport);
dayjs.locale('en');
dayjs.extend(isSameOrBefore);

export default {
  components: { NewAccountView, ConfirmDialog, PageLink, TenantSelect },
  data() {
    return {
      hideMenuByRoute: false,
      notification: {
        color: '',
        text: '',
        buttonText: '',
        showButton: false,
        timeOut: -1,
        callback: () => {},
      },
      loadingUserLanguage: false,
      snackbar: false,
      miniMenu: false,
      bottomNavSelected: '',
      showBottomNav: true,
      menuOpenState: false,
      pages: [],
    };
  },
  methods: {
    getPageLink(page) {
      const link = {
        name: page.link,
      };

      if ('params' in page) {
        link.params = page.params();
      }
      return link;
    },
    logout() {
      UiEventBus.$emit('userLoggedOut');
    },
    goToLink(name) {
      this.$router.push({ name: name });
    },
    toggleMiniMenu() {
      if (this.$vuetify.breakpoint.lgAndUp) {
        this.miniMenu = !this.miniMenu;
      } else {
        this.$store.commit('toggleMainMenuState');
      }
    },
    closeMenuIfOnSamePage(link) {
      if (this.$vuetify.breakpoint.mdAndDown && this.$route.name === link) {
        this.menuOpenState = false;
      }
    },
    handleSelectedBottomNavItem(name) {
      const isPagePrimary = this.pages.findIndex(p => p.primary && p.link === name) > -1;
      if (isPagePrimary) {
        this.bottomNavSelected = name;
        this.showBottomNav = true;
      } else {
        this.showBottomNav = false;
      }
    },
    async changeUserLanguage(language) {
      this.loadingUserLanguage = true;
      let done = false;
      try {
        await UserService.setUserLanguage(language);
        done = true;
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_language_change_failed'));
      }
      this.loadingUserLanguage = false;
      if (done) window.location.reload();
    },
  },
  watch: {
    $isLoggedIn(newValue, oldValue) {
      console.log('isLoggedInChange', newValue, oldValue);
      if (newValue === true) {
        UiEventBus.$emit('loginSuccess');
      }
    },
    loadingOverlay(to) {
      if (to) {
        setTimeout(() => {
          this.$store.commit('setLoadingOverlay', false);
        }, 2000);
      }
    },
    notification() {
      this.snackbar = true;
    },
    menuOpenComputed(to) {
      this.menuOpenState = to;
    },
    menuOpenState(to) {
      if (to !== this.$store.state.mainMenuOpen) {
        this.$store.commit('toggleMainMenuState');
      }
    },
    $route(to, from) {
      if (from && from.name) this.$store.commit('setPreviousRouteName', from.name);
      this.handleSelectedBottomNavItem(to.name);
    },
    currentRoute(val) {
      this.hideMenuByRoute = val.meta['hideMenu'] === true;
    },
    currentRouteName(val) {
      if (!this.$isLoggedIn && val !== 'login') {
        this.$router.push({ name: 'login' });
      }
    },
  },
  computed: {
    menuCount() {
      return this.$store.state.menuCount;
    },
    isUnreadMessages() {
      if (this.$store.state.menuCount.latestMessage?.updatedAt) {
        let latestRead = this.$store.state.latestMessagesRead;
        if (latestRead) {
          if (dayjs(latestRead).isBefore(this.$store.state.menuCount.latestMessage.updatedAt)) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
      return false;
    },
    onboardingStep() {
      const onboardingStep = this.$currentTenant?.values?.find(v => v.key === 'onboarding');
      if (onboardingStep) return parseInt(onboardingStep.value);
      return -1;
    },
    onboardingProgress() {
      return Math.round((this.onboardingStep / 7) * 100);
    },
    tenantAdminPages() {
      return [
        {
          title: this.$t('tenants.management_title'),
          icon: 'settings',
          link: 'tenant_settings',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: ['tenant_admin'],
          primary: false,
        },
        {
          title: this.$t('tenants.organization_users'),
          icon: 'people',
          link: 'tenant_users',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: ['tenant_admin'],
          primary: false,
        },
      ];
    },
    tenantPages() {
      return [
        {
          title: this.$t('dashboard.title'),
          icon: 'mdi mdi-gauge',
          link: 'tenant_start',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: [],
          primary: true,
        },
        {
          title: this.$tc('surveys.title', 2),
          icon: 'mdi mdi-clipboard-list-outline',
          link: 'tenant_surveys',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: ['tenant_admin', 'tenant_readonly'],
          count: () => this.menuCount.ongoingSurveys,
          primary: true,
        },
        {
          title: this.$tc('reports.title', 2),
          icon: 'mdi mdi-chart-box-outline',
          link: 'tenant_reports',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: [],
          primary: true,
        },
        {
          title: this.$tc('financials.title', 2),
          icon: 'mdi mdi-chart-timeline-variant-shimmer',
          link: 'tenant_financials',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: [],
          primary: true,
          requiresProOrHigher: false,
        },
        {
          title: this.$tc('tasks.title', 2),
          icon: 'mdi mdi-format-list-checks',
          link: 'tenant_tasks',
          params: () => ({ tenantId: TenantService.tenant }),
          tenantRoles: [],
          count: () => this.menuCount.undoneTasks,
          primary: true,
        },
        /*{
          title: this.$tc('message_center.message', 2),
          icon: 'mdi mdi-message-bulleted',
          link: 'tenant_messages',
          params: () => ({tenantId: TenantService.tenant}),
          tenantRoles: [],
          //count: this.isUnreadMessages,
          unread: this.isUnreadMessages,
          primary: true,
        },*/
        /*{
          title: 'Tenant Test',
          icon: 'people',
          link: 'tenant_test',
          params: () => ({tenantId: TenantService.tenant}),
          roles: [],
          primary: true,
        },*/
        /*{
          title: this.$tc('items.title', 2),
          icon: 'mdi mdi-clipboard-list-outline',
          iconSize: '20px',
          link: 'tenant_items',
          params: () => ({tenantId: TenantService.tenant}),
          roles: ['tenant_admin'],
          primary: true,
        },*/
        /*{
          title: this.$tc('party.title', 2),
          icon: 'mdi mdi-domain',
          iconSize: '20px',
          link: 'tenant_parties',
          params: () => ({tenantId: TenantService.tenant}),
          roles: ['tenant_admin'],
          primary: true,
        },
        {
          title: this.$tc('person.title', 2),
          icon: 'mdi mdi-card-account-details-outline',
          iconSize: '20px',
          link: 'tenant_persons',
          params: () => ({tenantId: TenantService.tenant}),
          roles: ['tenant_admin'],
          primary: true,
        },*/
      ];
    },

    adminPages() {
      return [
        /*{
          title: this.$t('message_center.title'),
          icon: 'mdi mdi-message-bulleted',
          link: 'adminmessagecenter',
          roles: ['ADMIN'],
          primary: false,
        },*/
        {
          title: this.$t('user_management.title'),
          icon: 'people',
          link: 'users',
          roles: ['ADMIN'],
          primary: false,
        },
        {
          title: this.$t('tenants.title'),
          icon: 'business',
          link: 'tenants',
          roles: ['ADMIN'],
          primary: false,
        },
        {
          title: this.$tc('translations.title', 2),
          icon: 'mdi mdi-translate',
          link: 'translations',
          roles: ['ADMIN'],
          primary: false,
        },
        {
          title: this.$tc('categories.title', 2),
          icon: 'mdi mdi-shape',
          link: 'categories',
          roles: ['ADMIN'],
          primary: false,
        },
        {
          title: this.$tc('survey_templates.title', 1),
          icon: 'mdi mdi-clipboard-multiple-outline',
          link: 'survey_templates',
          roles: ['ADMIN'],
          primary: false,
        },
        {
          title: this.$t('tasks_management.title'),
          icon: 'mdi mdi-playlist-edit',
          link: 'tasks_management',
          roles: ['ADMIN'],
          primary: false,
        },
      ];
    },
    languages() {
      return TranslationService.languages
        .filter(l => ['fi', 'en'].includes(l))
        .map(l => {
          return {
            text: this.$t('translations.select_languages.' + l),
            value: l,
            icon: l === 'en' ? 'gb' : l,
          };
        });
    },
    userLanguage() {
      return TranslationService.language;
    },
    showApplication() {
      return !this.$isLoggedIn || this.$isAdmin || (this.$userInfo && this.$userInfo.tenants.length > 0);
    },
    loadingOverlay() {
      return this.$store.state.loadingOverlay;
    },
    shouldMenuBeAvailable() {
      return !this.hideMenuByRoute && this.$isLoggedIn;
    },
    menuOpenComputed() {
      let menuState = true;
      if (this.$vuetify.breakpoint.mdAndDown) {
        menuState = this.$store.state.mainMenuOpen;
      }
      return menuState;
    },
    filteredSecondaryPages() {
      let pages = this.pages.filter(p => !p.primary);

      if (this.$tenant) {
        pages = [...pages, ...this.tenantPages.filter(tp => !tp.primary)];
      }
      return pages;
    },
    filteredTenantAdminPages() {
      if (this.$tenant && this.$hasMatchingTenantRoles(['tenant_admin'])) {
        return this.tenantAdminPages;
      }
      return [];
    },
    filteredAdminPages() {
      if (this.$isAdmin) {
        return this.adminPages;
      }
      return [];
    },
    filteredPrimaryPages() {
      let pages = this.pages.filter(p => p.primary);

      if (this.$tenant) {
        pages = [...pages, ...this.tenantPages.filter(tp => tp.primary)];
      }
      return pages;
    },
    currentRoute() {
      return this.$route;
    },
    currentRouteName() {
      return this.$route.name;
    },
  },
  created() {
    UiEventBus.$on('showNotification', payload => {
      this.notification = {
        color: payload.color,
        text: payload.message,
        timeOut: payload.timeOut !== undefined ? payload.timeOut : 4000,
        showButton: payload.showButton !== undefined ? payload.showButton : false,
        buttonText: payload.buttonText !== undefined ? payload.buttonText : '',
        callback: payload.showButton && payload.callback ? payload.callback : () => {},
      };
    });
    // If application is opened from link, and some other tenant id has been saved in tenant service
    // This sets the one from URL
    if ('tenantId' in this.$router.currentRoute.params) {
      TenantService.setTenant(this.$router.currentRoute.params.tenantId);
    }
  },

  async mounted() {
    if (this.$userInfo) {
      if (Sentry) {
        Sentry.setUser({ email: this.$userInfo.email });
      }
    }
    if (this.$vuetify.breakpoint.lgAndUp) this.menuOpenState = true;
    this.handleSelectedBottomNavItem(this.$route.name);
    this.$root.$confirm = this.$refs.confirm.open;
    this.hideMenuByRoute = this.$route.meta['hideMenu'] === true;
    await this.$store.commit('setUserInfo', this.$userInfo);
    await this.$store.commit('setLatestMessagesRead');
    setTimeout(() => {
      // Set timeout to let it go to bottom of the event queue
      this.$store.dispatch('reloadMenuCount');
    }, 0);
  },
};
</script>
<style lang="scss">
@import './sass/commonstyles.scss';

@media screen and (max-width: 959px) {
  .main-menu {
    max-height: calc(100% - 75px);
    overflow: auto;
  }
}

.language-select.v-text-field > .v-input__control > .v-input__slot {
  &:before {
    border-style: none;
  }

  &:after {
    border-style: none;
  }
}
</style>
